<template>
  <div>
    <div
      v-if="!hideHeader"
      class="mb-1 d-flex justify-content-end align-items-center w-full"
      style="gap: 0.5rem"
    >
      <div class="position-relative">
        <b-button
          variant="outline-primary"
          :pressed="isFilterVisible"
          class="d-flex align-items-center"
          @click="isFilterVisible = !isFilterVisible"
        >
          <feather-icon icon="FilterIcon" size="14" class="mr-50" />
          {{ $t('Filters') }}
          <div
            v-if="filterCount > 0"
            class="d-flex align-items-center badge-container"
            :class="{ 'badge-pulse': shouldPulse }"
          >
            {{ filterCount }}
          </div>
        </b-button>
      </div>

      <slot name="extra-actions"></slot>
    </div>
    <b-collapse v-model="isFilterVisible">
      <div class="app-filter">
        <b-form ref="filterForm" method="post" @submit.prevent>
          <b-row>
            <!-- Optional date range filter as first column -->
            <b-col v-if="$slots['date-filter']" :sm="6" :lg="3">
              <b-form-group v-if="dateFilterLabel" :label="dateFilterLabel">
                <slot name="date-filter"></slot>
              </b-form-group>
              <slot v-else name="date-filter"></slot>
            </b-col>

            <b-col
              v-for="filter in filters"
              :key="filter.id"
              :sm="filter.sm || 6"
              :lg="filter.lg || 3"
            >
              <b-form-group :label="$t(filter.label)" :label-for="filter.id">
                <!-- Custom Date Range Filter -->
                <DateRangeFilter
                  v-if="filter.type === 'dateRange'"
                  :value="getDateRangeValue(filter)"
                  @input="onDateRangeChange(filter, $event)"
                />

                <!-- Select Input -->
                <v-select
                  v-else-if="filter.type === 'select'"
                  v-model="draftFilters[filter.id]"
                  :options="
                    (asyncOptionsByFilterId[filter.id] &&
                      asyncOptionsByFilterId[filter.id].options) ||
                    filter.options
                  "
                  :multiple="filter.multiple"
                  :searchable="true"
                  :loading="
                    asyncOptionsByFilterId[filter.id]
                      ? asyncOptionsByFilterId[filter.id].loading
                      : filter.loading
                  "
                  :placeholder="$t(filter.placeholder)"
                  :get-option-label="option => option.label || option.text || option"
                  :reduce="option => (option.value !== undefined ? option.value : option)"
                  @input="handleInput(filter.id, $event)"
                />

                <!-- Text Input -->
                <b-form-input
                  v-else-if="filter.type === 'text'"
                  v-model="draftFilters[filter.id]"
                  :name="filter.id"
                  :placeholder="$t(filter.placeholder)"
                  :type="filter.inputType || 'text'"
                  :formatter="filter.formatter"
                  autocomplete="off"
                  @keyup.enter="applyFilters"
                />

                <!-- Date -->
                <b-input-group v-else-if="filter.type === 'date'" class="input-group-merge">
                  <flat-pickr
                    v-model="draftFilters[filter.id]"
                    class="form-control"
                    name="dateFrom"
                    :placeholder="$t(filter.placeholder)"
                    :config="{
                      enableTime: false,
                      dateFormat: 'd-m-Y',
                      locale: flatpickrLocale
                    }"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      icon="XIcon"
                      class="cursor-pointer"
                      @click="draftFilters[filter.id] = ''"
                    />
                  </b-input-group-append>
                </b-input-group>

                <!-- Date Time -->
                <b-input-group v-else-if="filter.type === 'dateTime'" class="input-group-merge">
                  <flat-pickr
                    v-model="draftFilters[filter.id]"
                    class="form-control"
                    name="dateFrom"
                    :placeholder="$t(filter.placeholder)"
                    :config="{
                      enableTime: true,
                      enableSeconds: true,
                      time_24hr: true,
                      dateFormat: 'd-m-Y H:i:S',
                      locale: flatpickrLocale
                    }"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      icon="XIcon"
                      class="cursor-pointer"
                      @click="draftFilters[filter.id] = ''"
                    />
                  </b-input-group-append>
                </b-input-group>

                <!-- Date Range Picker -->
                <b-input-group v-else-if="filter.type === 'daterange'" class="input-group-merge">
                  <flat-pickr
                    v-model="draftFilters[filter.id]"
                    class="form-control"
                    name="dateFrom"
                    :placeholder="$t(filter.placeholder)"
                    :config="{
                      enableTime: false,
                      mode: 'range',
                      dateFormat: 'd-m-Y',
                      locale: flatpickrLocale
                    }"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      icon="XIcon"
                      class="cursor-pointer"
                      @click="draftFilters[filter.id] = ''"
                    />
                  </b-input-group-append>
                </b-input-group>

                <!-- Numeric Range -->
                <div v-else-if="filter.type === 'range'" class="range-inputs">
                  <b-form-input
                    v-model.number="draftFilters[filter.id].min"
                    type="number"
                    :placeholder="$t('min')"
                  />
                  <b-form-input
                    v-model.number="draftFilters[filter.id].max"
                    type="number"
                    :placeholder="$t('max')"
                  />
                </div>
              </b-form-group>
            </b-col>
          </b-row>
        </b-form>

        <!-- Active Filters -->
        <div class="d-flex justify-between">
          <div
            class="d-flex flex-wrap flex-grow-1 justify-content-start align-items-center"
            style="gap: 0.5rem"
          >
            <template v-if="hasActiveFilters">
              <b-badge
                v-for="(value, key) in activeFilters"
                :key="key"
                variant="secondary"
                class="filter-badge"
              >
                {{ getFilterLabel(key) }}: {{ formatFilterValue(value, getFilterForKey(key)) }}
                <feather-icon icon="XIcon" class="cursor-pointer" @click="removeFilter(key)" />
              </b-badge>
            </template>
          </div>
          <div class="d-flex justify-content-end flex-1 align-items-center" style="gap: 0.5rem">
            <b-button
              variant="outline-primary"
              size="sm"
              :disabled="filterSubmitting"
              block
              @click="applyFilters"
            >
              {{ filterSubmitting ? $t('Please Wait') : $t('Apply') }}
            </b-button>
            <b-button
              v-if="hasActiveFilters"
              variant="outline-danger"
              :disabled="!hasActiveFilters || filterSubmitting"
              size="sm"
              @click="clearFilters"
            >
              {{ $t('Clear') }}
            </b-button>
          </div>
        </div>
      </div>
    </b-collapse>
  </div>
</template>

<script>
import {
  BForm,
  BFormGroup,
  BFormInput,
  BButton,
  BRow,
  BCol,
  BBadge,
  BInputGroup,
  BInputGroupAppend,
  BCollapse
} from 'bootstrap-vue';
import flatPickr from 'vue-flatpickr-component';
import vSelect from 'vue-select';
import { helper } from '@core/mixins/helper';
import DateRangeFilter from '@/components/DateRangeFilter/DateRangeFilter.vue';
import { Turkish } from 'flatpickr/dist/l10n/tr.js';

export default {
  name: 'AppFilter',

  components: {
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BRow,
    BCol,
    BBadge,
    BInputGroup,
    BInputGroupAppend,
    BCollapse,
    vSelect,
    flatPickr,
    DateRangeFilter
  },

  mixins: [helper],

  props: {
    title: {
      type: String,
      default: 'Filters'
    },
    filters: {
      type: Array,
      required: true
    },
    filterSubmitting: {
      type: Boolean,
      default: false
    },
    hideHeader: {
      type: Boolean,
      default: false
    },
    initialValues: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      isFilterVisible: true,
      draftFilters: {},
      selectedFilters: {},
      shouldPulse: false,
      previousFilterCount: 0,
      asyncOptionsByFilterId: {}
    };
  },

  computed: {
    isTurkish() {
      return this.$i18n.locale === 'tr';
    },
    flatpickrLocale() {
      return this.isTurkish ? Turkish : 'en';
    },
    filterCount() {
      return Object.keys(this.selectedFilters).length;
    },
    hasActiveFilters() {
      return Object.keys(this.selectedFilters).some(
        key => this.selectedFilters[key] !== null && this.selectedFilters[key] !== ''
      );
    },

    activeFilters() {
      return Object.entries(this.selectedFilters)
        .filter(([_, value]) => value !== null && value !== '')
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
    }
  },

  watch: {
    filterCount: {
      handler(newCount, oldCount) {
        if (newCount !== oldCount) {
          this.shouldPulse = true;
          setTimeout(() => {
            this.shouldPulse = false;
          }, 300);
        }
      },
      immediate: true
    },
    initialValues: {
      deep: true,
      handler(newVal) {
        this.syncInitialValues(newVal || {});
      }
    }
  },

  methods: {
    syncInitialValues(initial) {
      const safe = JSON.parse(JSON.stringify(initial || {}));

      // Convert Y-m-d back to d-m-Y for UI (because applyFilters converts to Y-m-d)
      this.filters.forEach(f => {
        if (
          ['date', 'dateTime', 'daterange'].includes(f.type) &&
          safe[f.id] &&
          typeof safe[f.id] === 'string'
        ) {
          safe[f.id] = safe[f.id].replace(/(\d{4})-(\d{2})-(\d{2})/g, '$3-$2-$1');
        }
      });

      this.draftFilters = { ...safe };
      this.selectedFilters = { ...this.getFilteredObject(safe) };
    },
    async getFilterOptions(filter) {
      if (filter.asyncOptions) {
        try {
          this.asyncOptionsByFilterId = {
            ...this.asyncOptionsByFilterId,
            [filter.id]: {
              loading: true,
              options: []
            }
          };

          const options = await filter.asyncOptions(this.draftFilters);

          this.asyncOptionsByFilterId = {
            ...this.asyncOptionsByFilterId,
            [filter.id]: {
              loading: false,
              options
            }
          };
        } catch (error) {
          this.$delete(this.asyncOptionsByFilterId, filter.id);
        }
      }
    },

    checkAndClearChildKeys(currentFilter) {
      if (currentFilter?.childKeys) {
        currentFilter.childKeys.forEach(childKey => {
          this.$delete(this.draftFilters, childKey);
          this.getFilterOptions(this.getFilterById(childKey));
          if (this.getFilterById(childKey)?.childKeys) {
            this.checkAndClearChildKeys(this.getFilterById(childKey));
          }
        });
      }
    },

    handleInput(id, value) {
      let finalValue = value;
      if (Array.isArray(value) && value.includes('ALL') && value.length > 1) {
        const prevHasAll = (this.selectedFilters[id] || []).includes('ALL');
        finalValue = prevHasAll ? value.filter(v => v !== 'ALL') : ['ALL'];
      }
      this.draftFilters[id] = finalValue;
      const currentFilter = this.getFilterById(id);
      if (currentFilter?.immediate || currentFilter?.childKeys) {
        if (currentFilter?.childKeys) {
          this.checkAndClearChildKeys(currentFilter);
        }
        this.applyFilters();
      }
    },

    getDateRangeValue(filter) {
      const keys = filter.dateKeys || {};
      return {
        dateFrom: this.draftFilters[keys.dateFrom] || '',
        dateTo: this.draftFilters[keys.dateTo] || '',
        dropOffDateFrom: this.draftFilters[keys.dropOffDateFrom] || '',
        dropOffDateTo: this.draftFilters[keys.dropOffDateTo] || ''
      };
    },

    onDateRangeChange(filter, range) {
      const keys = filter.dateKeys || {};
      if (keys.dateFrom) this.$set(this.draftFilters, keys.dateFrom, range.dateFrom || '');
      if (keys.dateTo) this.$set(this.draftFilters, keys.dateTo, range.dateTo || '');
      if (keys.dropOffDateFrom)
        this.$set(this.draftFilters, keys.dropOffDateFrom, range.dropOffDateFrom || '');
      if (keys.dropOffDateTo)
        this.$set(this.draftFilters, keys.dropOffDateTo, range.dropOffDateTo || '');
      // Apply immediately so badges and clear button update when date range changes
      this.applyFilters();
    },

    applyFilters() {
      const filteredValues = this.getFilteredObject(this.draftFilters);
      this.selectedFilters = { ...filteredValues };

      const payload = JSON.parse(JSON.stringify(filteredValues));

      // Convert d-m-Y back to Y-m-d for backend
      this.filters.forEach(f => {
        if (['date', 'dateTime', 'daterange'].includes(f.type) && payload[f.id]) {
          payload[f.id] = payload[f.id].replace(/(\d{2})-(\d{2})-(\d{4})/g, '$3-$2-$1');
        }
      });

      this.$emit('filter', payload);
    },

    clearFilters() {
      this.draftFilters = {};
      this.selectedFilters = {};
      this.$emit('filter', {});
    },

    removeFilter(key) {
      this.$delete(this.selectedFilters, key);
      this.$delete(this.draftFilters, key);
      this.applyFilters();
    },

    getFilterById(id) {
      return this.filters.find(f => f.id === id);
    },

    getFilterForKey(key) {
      // First try to find a direct filter with this id
      let filter = this.getFilterById(key);

      // If not found, it might belong to a dateRange filter's dateKeys
      if (!filter) {
        filter = this.filters.find(
          f => f.type === 'dateRange' && f.dateKeys && Object.values(f.dateKeys).includes(key)
        );
      }

      return filter || null;
    },

    getFilterLabel(key) {
      const filter = this.getFilterForKey(key);
      if (!filter) return key;

      // For dateRange, map specific keys to shared i18n labels
      if (filter.type === 'dateRange' && filter.dateKeys) {
        const entry = Object.entries(filter.dateKeys).find(([, value]) => value === key);
        if (entry) {
          const logicalKey = entry[0];
          const dateLabelMap = {
            dateFrom: 'Creation Start Date',
            dateTo: 'Creation End Date',
            dropOffDateFrom: 'Drop Off Start Date',
            dropOffDateTo: 'Drop Off End Date'
          };
          const labelKey = dateLabelMap[logicalKey];
          if (labelKey) {
            return this.$t(labelKey);
          }
        }
      }

      return this.$t(filter.label);
    },

    formatFilterValue(value, filter) {
      if (!filter) return value;
      const filterOptions = this.asyncOptionsByFilterId[filter.id] || filter.options || [];
      if (Array.isArray(value)) {
        if (filter.type === 'select') {
          return value
            .map(v => {
              const opt = filterOptions.find(o => o.value === v);
              return opt ? opt.label || opt.text || v : v;
            })
            .join(', ');
        }
        return value.join(', ');
      }
      if (typeof value === 'object') return `${value.min} - ${value.max}`;
      if (
        (filter.type === 'dateRange' || filter.type === 'daterange') &&
        typeof value === 'string'
      ) {
        return this.formatDateDisplay(value);
      }
      if (filter && filter.type === 'select') {
        const option = filterOptions.find(opt => opt.value === value);
        return option ? option.label || option.text || value : value;
      }
      return value;
    },

    formatDateDisplay(value) {
      if (!value || typeof value !== 'string') return value;

      let separator = null;
      if (value.includes(' to ')) {
        separator = ' to ';
      } else if (value.includes(' - ')) {
        separator = ' - ';
      }

      if (separator) {
        const [start, end] = value.split(separator);
        return `${this.formatSingleDate(start)} - ${this.formatSingleDate(end)}`;
      }
      return this.formatSingleDate(value);
    },

    formatSingleDate(dateStr) {
      const parts = dateStr.trim().split('-');
      if (parts.length !== 3) return dateStr;

      if (parts[0].length === 4) {
        const [year, month, day] = parts;
        return `${day}-${month}-${year}`;
      }

      return dateStr.trim();
    }
  },

  mounted() {
    // Initialize from initialValues on mount
    if (this.initialValues && Object.keys(this.initialValues).length) {
      this.syncInitialValues(this.initialValues);
    }

    this.filters.forEach(filter => {
      this.getFilterOptions(filter);
    });
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>

<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';

.app-filter {
  padding: 1rem;
  margin-bottom: 1rem;
  border: 1px solid #ebe9f1;

  .dark-layout & {
    border-color: $theme-dark-border-color;
  }
  border-radius: 0.5rem;

  .filter-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  .filter-actions {
    display: flex;
    gap: 0.5rem;
    align-items: flex-start;
  }

  .range-inputs {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
  }

  .active-filters {
    &-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.5rem;
    }
  }

  .create-new-btn {
    width: 40px;
    padding: 0.5rem;
  }
}

.collapse-enter-active,
.collapse-leave-active {
  transition: all 0.3s ease-in-out;
}

.collapse-enter,
.collapse-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.badge-container {
  position: absolute;
  top: -5px;
  right: -5px;
  width: 20px;
  height: 20px;
  border-radius: 100%;
  margin-left: 0.5rem;
  background-color: var(--danger);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  transition: all 0.3s ease;

  &.badge-pulse {
    animation: pulse 0.3s 1;
  }
}

.filter-badge {
  animation: slideIn 0.3s ease-out;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(var(--danger-rgb), 0.7);
  }

  70% {
    transform: scale(1.2);
    box-shadow: 0 0 0 10px rgba(var(--danger-rgb), 0);
  }

  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(var(--danger-rgb), 0);
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
